import React, { useMemo } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Divider,
} from '@mui/material';
import {
  Business,
  CheckCircle,
  Warning,
  Error,
  CalendarToday,
} from '@mui/icons-material';
import './ActivityFeed.css';

interface ActivityItem {
  companyName: string;
  planType: string | null;
  planDate: string;
  remainingDays?: number;
  planStatus: 'expired' | 'renewed' | 'expiring';
}

interface ActivityFeedProps {
  activities: ActivityItem[] | null;
  loading: boolean;
}

function shuffleArray(array: any[]) {
  const shuffled = [...array];

  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

export const ActivityFeed: React.FC<ActivityFeedProps> = ({
  activities,
  loading = false,
}) => {
  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'expired':
        return {
          color: '#dc2626',
          bgColor: '#fef2f2',
          icon: <Error className="adminActivityStatusIcon" />,
          label: 'Expired',
        };
      case 'expiring':
        return {
          color: '#f59e0b',
          bgColor: '#fffbeb',
          icon: <Warning className="adminActivityStatusIcon" />,
          label: 'Expiring Soon',
        };
      case 'renewed':
        return {
          color: '#059669',
          bgColor: '#f0fdf4',
          icon: <CheckCircle className="adminActivityStatusIcon" />,
          label: 'Renewed',
        };
      default:
        return {
          color: '#64748b',
          bgColor: '#f8fafc',
          icon: <CalendarToday className="adminActivityStatusIcon" />,
          label: 'Unknown',
        };
    }
  };

  const getStatusMessage = (activity: ActivityItem) => {
    if (activity.planStatus === 'expired') {
      if (activity.remainingDays && activity.remainingDays < -30) {
        return `Expired ${Math.abs(activity.remainingDays)} days ago`;
      }
      return 'Plan has expired';
    }
    if (activity.planStatus === 'expiring' && activity.remainingDays) {
      return `Expires in ${activity.remainingDays} ${
        activity.remainingDays === 1 ? 'day' : 'days'
      }`;
    }
    if (activity.planStatus === 'renewed') {
      return 'Plan successfully renewed';
    }
    return '';
  };

  const shuffledArray =
    activities && useMemo(() => shuffleArray(activities), [activities]);

  return (
    <Card className="activityFeedCard">
      <CardContent className="activityFeedContent">
        <Box className="activityFeedHeader">
          <Typography variant="h6" className="activityFeedTitle">
            Recent Activity
          </Typography>
          <Typography variant="body2" className="activityFeedSubtitle">
            {activities?.length}{' '}
            {activities?.length === 1 ? 'update' : 'updates'}
          </Typography>
        </Box>

        <Divider className="activityDivider" />

        <Box className="activityList">
          {loading ? (
            <Box className="emptyState">
              <Typography variant="body2" className="emptyStateText">
                Loading...
              </Typography>
            </Box>
          ) : activities?.length === 0 ? (
            <Box className="emptyState">
              <Typography variant="body2" className="emptyStateText">
                No recent activity
              </Typography>
            </Box>
          ) : (
            activities &&
            shuffledArray?.map((activity, index) => {
              const statusConfig = getStatusConfig(activity.planStatus);
              return (
                <Box key={index} className="activityItem">
                  <Box className="activityItemLeft">
                    <div
                      className="activityIconWrapper"
                      style={{ backgroundColor: statusConfig.bgColor }}
                    >
                      <Business
                        className="activityCompanyIcon"
                        style={{ color: statusConfig.color }}
                      />
                    </div>

                    <Box className="activityDetails">
                      <Typography
                        variant="body1"
                        className="activityCompanyName"
                      >
                        {activity.companyName}
                      </Typography>
                      <Box className="activityMeta">
                        {activity.planType && (
                          <Chip
                            label={activity.planType}
                            size="small"
                            className="planTypeChip"
                          />
                        )}
                        <Typography variant="caption" className="activityDate">
                          {activity.planDate}
                        </Typography>
                      </Box>
                      <Typography variant="body2" className="activityMessage">
                        {getStatusMessage(activity)}
                      </Typography>
                    </Box>
                  </Box>

                  <Box className="activityItemRight">
                    <Chip
                      icon={statusConfig.icon}
                      label={statusConfig.label}
                      size="small"
                      className="statusChip"
                      style={{
                        backgroundColor: statusConfig.bgColor,
                        color: statusConfig.color,
                      }}
                    />
                  </Box>
                </Box>
              );
            })
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

// Usage example:
/*
const sampleData = [
  {
    companyName: 'Gefen',
    planType: null,
    planDate: 'Invalid date',
    remainingDays: -20367,
    status: 'expired',
  },
  {
    companyName: 'cfx himani',
    planType: 'Basic',
    planDate: '25 Sep 2025',
    remainingDays: -4,
    status: 'expired',
  },
  // ... more items
];

<ActivityFeed activities={sampleData} />
*/
